"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import Select, { type MultiValue } from "react-select";
import { ChevronUp, ChevronDown, X, SlidersHorizontal } from "lucide-react";
import { TeamLogo } from "@/components/ui/TeamLogo";
import { Modal } from "@/components/ui/modals/Modal";
import { useLabels } from "@/lib/labels/LabelsProvider";
import { cn } from "@/utilities/core/cn";
import { fetchFootballCompetitions, type FootballCompetition } from "@/app/(app)/actions/fetchFootballCompetitions";
import type { LocaleCode } from "@/lib/labels/types";
interface LivescoreLeaguePickerProps {
  isOpen: boolean;
  onClose: () => void;
  locale: LocaleCode;
  initialFavouriteIds: string[];
  onSave: (ids: string[]) => void;
}
interface CompOption {
  value: string; // competition.id (native FansUnited ID, e.g. "fb:c:3")
  label: string; // competition name (used by react-select for filtering)
  comp: FootballCompetition;
}
function getCompDisplayName(comp: FootballCompetition): string {
  return comp.gender === "female" ? `${comp.name} (W)` : comp.name;
}

/** Custom option renderer: logo + name + country */
function formatOptionLabel(option: CompOption) {
  return <div className="flex items-center gap-2 py-0.5 cursor-pointer">
            {option.comp.logoUrl ? <TeamLogo src={option.comp.logoUrl} size="sm" kind="competition" competitionId={option.comp.id} /> : <div className="w-5 h-5 shrink-0 rounded-full bg-muted" />}
            <span className="flex-1 truncate">{getCompDisplayName(option.comp)}</span>
            {option.comp.countryName && <span className="text-xs text-muted-foreground shrink-0">{option.comp.countryName}</span>}
        </div>;
}
export function LivescoreLeaguePicker(t0) {
  const $ = _c(74);
  const {
    isOpen,
    onClose,
    locale,
    initialFavouriteIds,
    onSave
  } = t0;
  const labels = useLabels();
  const t = labels.livescore.picker;
  const ls = labels.livescore;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [competitions, setCompetitions] = useState(t1);
  const [loadingComps, setLoadingComps] = useState(false);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [pendingIds, setPendingIds] = useState(t2);
  const [portalTarget, setPortalTarget] = useState(null);
  const hasLoadedRef = useRef(false);
  let t3;
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      setPortalTarget(document.body);
    };
    t4 = [];
    $[2] = t3;
    $[3] = t4;
  } else {
    t3 = $[2];
    t4 = $[3];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[4] !== isOpen || $[5] !== locale) {
    t5 = () => {
      if (!isOpen || hasLoadedRef.current) {
        return;
      }
      hasLoadedRef.current = true;
      setLoadingComps(true);
      fetchFootballCompetitions(locale).then(setCompetitions).catch(() => setCompetitions([])).finally(() => setLoadingComps(false));
    };
    t6 = [isOpen, locale];
    $[4] = isOpen;
    $[5] = locale;
    $[6] = t5;
    $[7] = t6;
  } else {
    t5 = $[6];
    t6 = $[7];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[8] !== initialFavouriteIds || $[9] !== isOpen) {
    t7 = () => {
      if (isOpen) {
        setPendingIds([...initialFavouriteIds]);
      }
    };
    t8 = [isOpen, initialFavouriteIds];
    $[8] = initialFavouriteIds;
    $[9] = isOpen;
    $[10] = t7;
    $[11] = t8;
  } else {
    t7 = $[10];
    t8 = $[11];
  }
  useEffect(t7, t8);
  let map;
  if ($[12] !== competitions) {
    map = new Map();
    for (const c of competitions) {
      map.set(c.id, c);
    }
    $[12] = competitions;
    $[13] = map;
  } else {
    map = $[13];
  }
  const compById = map;
  let t9;
  if ($[14] !== competitions) {
    t9 = competitions.map(_temp).sort(_temp2);
    $[14] = competitions;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const selectOptions = t9;
  let t10;
  if ($[16] !== pendingIds || $[17] !== selectOptions) {
    let t11;
    if ($[19] !== selectOptions) {
      t11 = id => selectOptions.find(o => o.value === id);
      $[19] = selectOptions;
      $[20] = t11;
    } else {
      t11 = $[20];
    }
    t10 = pendingIds.map(t11).filter(Boolean);
    $[16] = pendingIds;
    $[17] = selectOptions;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  const selectedOptions = t10 as CompOption[];
  let t11;
  if ($[21] !== compById || $[22] !== pendingIds) {
    let t12;
    if ($[24] !== compById) {
      t12 = id_0 => compById.get(id_0);
      $[24] = compById;
      $[25] = t12;
    } else {
      t12 = $[25];
    }
    t11 = pendingIds.map(t12).filter(Boolean);
    $[21] = compById;
    $[22] = pendingIds;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  const selectedComps = t11 as FootballCompetition[];
  let t12;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = newValue => {
      const newIds = newValue.map(_temp3);
      setPendingIds(prev => {
        const kept = prev.filter(id_1 => newIds.includes(id_1));
        const added = newIds.filter(id_2 => !prev.includes(id_2));
        return [...kept, ...added];
      });
    };
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  const handleSelectChange = t12;
  let t13;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = id_3 => {
      setPendingIds(prev_0 => prev_0.filter(x => x !== id_3));
    };
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  const remove = t13;
  let t14;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = index => {
      if (index === 0) {
        return;
      }
      setPendingIds(prev_1 => {
        const next = [...prev_1];
        const [t15, t16] = [next[index], next[index - 1]];
        next[index - 1] = t15;
        next[index] = t16;
        return next;
      });
    };
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  const moveUp = t14;
  let t15;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = index_0 => {
      setPendingIds(prev_2 => {
        if (index_0 >= prev_2.length - 1) {
          return prev_2;
        }
        const next_0 = [...prev_2];
        const [t16, t17] = [next_0[index_0 + 1], next_0[index_0]];
        next_0[index_0] = t16;
        next_0[index_0 + 1] = t17;
        return next_0;
      });
    };
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  const moveDown = t15;
  let t16;
  if ($[30] !== onClose || $[31] !== onSave || $[32] !== pendingIds) {
    t16 = () => {
      onSave(pendingIds);
      onClose();
    };
    $[30] = onClose;
    $[31] = onSave;
    $[32] = pendingIds;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  const handleSave = t16;
  let t17;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <SlidersHorizontal className="w-4 h-4 text-muted-foreground shrink-0" />;
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  let t18;
  if ($[35] !== ls.filters.myLeagues) {
    t18 = <div className="px-5 pt-5 pb-3 border-b border-border shrink-0 flex items-center gap-2">{t17}<h2 className="text-base font-semibold">{ls.filters.myLeagues}</h2></div>;
    $[35] = ls.filters.myLeagues;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  let t19;
  if ($[37] !== ls.filters.myLeagues) {
    t19 = <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">{ls.filters.myLeagues}</p>;
    $[37] = ls.filters.myLeagues;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  let t20;
  if ($[39] !== selectedComps || $[40] !== t.addLeaguesHint) {
    t20 = selectedComps.length === 0 ? <p className="text-sm text-muted-foreground py-1">{t.addLeaguesHint}</p> : <ul className="space-y-0.5">{selectedComps.map((comp, idx) => <li key={comp.id} className="flex items-center gap-2 py-1.5 px-2 rounded-md hover:bg-muted/50">{(() => {
          const resolvedLogo = comp.logoUrl;
          return resolvedLogo ? <img src={resolvedLogo} alt="" className="w-5 h-5 object-contain shrink-0" /> : <div className="w-5 h-5 shrink-0 rounded-full bg-muted" />;
        })()}<span className="flex-1 text-sm truncate">{getCompDisplayName(comp)}</span>{comp.countryName && <span className="text-xs text-muted-foreground shrink-0">{comp.countryName}</span>}<div className="flex items-center gap-0.5 shrink-0 ml-1"><button onClick={() => moveUp(idx)} disabled={idx === 0} className="p-0.5 rounded hover:bg-muted disabled:opacity-30 cursor-pointer disabled:cursor-default" aria-label="Move up"><ChevronUp className="w-4 h-4" /></button><button onClick={() => moveDown(idx)} disabled={idx === selectedComps.length - 1} className="p-0.5 rounded hover:bg-muted disabled:opacity-30 cursor-pointer disabled:cursor-default" aria-label="Move down"><ChevronDown className="w-4 h-4" /></button><button onClick={() => remove(comp.id)} className="p-0.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground ml-0.5 cursor-pointer" aria-label="Remove"><X className="w-4 h-4" /></button></div></li>)}</ul>;
    $[39] = selectedComps;
    $[40] = t.addLeaguesHint;
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  let t21;
  if ($[42] !== t19 || $[43] !== t20) {
    t21 = <div className="px-5 pt-4 pb-4">{t19}{t20}</div>;
    $[42] = t19;
    $[43] = t20;
    $[44] = t21;
  } else {
    t21 = $[44];
  }
  let t22;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <div className="border-t border-border mx-5" />;
    $[45] = t22;
  } else {
    t22 = $[45];
  }
  let t23;
  if ($[46] !== labels.common.loading || $[47] !== labels.common.noResults || $[48] !== loadingComps || $[49] !== portalTarget || $[50] !== selectOptions || $[51] !== selectedOptions || $[52] !== t.searchPlaceholder) {
    t23 = <div className="px-5 pt-4 pb-5">{loadingComps ? <p className="text-sm text-muted-foreground py-4 text-center">{labels.common.loading}</p> : <Select isMulti={true} unstyled={true} instanceId="livescore-league-picker" options={selectOptions} value={selectedOptions} onChange={handleSelectChange} formatOptionLabel={formatOptionLabel} placeholder={t.searchPlaceholder} noOptionsMessage={() => labels.common.noResults} isClearable={false} closeMenuOnSelect={false} hideSelectedOptions={false} controlShouldRenderValue={false} menuPortalTarget={portalTarget} styles={{
        menuPortal: _temp4
      }} classNames={{
        control: _temp5,
        valueContainer: _temp6,
        placeholder: _temp7,
        input: _temp8,
        menu: _temp9,
        menuList: _temp10,
        option: _temp11,
        noOptionsMessage: _temp12,
        indicatorsContainer: _temp13
      }} />}</div>;
    $[46] = labels.common.loading;
    $[47] = labels.common.noResults;
    $[48] = loadingComps;
    $[49] = portalTarget;
    $[50] = selectOptions;
    $[51] = selectedOptions;
    $[52] = t.searchPlaceholder;
    $[53] = t23;
  } else {
    t23 = $[53];
  }
  let t24;
  if ($[54] !== t21 || $[55] !== t23) {
    t24 = <div className="flex-1 overflow-y-auto min-h-0">{t21}{t22}{t23}</div>;
    $[54] = t21;
    $[55] = t23;
    $[56] = t24;
  } else {
    t24 = $[56];
  }
  let t25;
  if ($[57] !== labels.common.close || $[58] !== onClose) {
    t25 = <button onClick={onClose} className="btn-plain px-4 py-2 text-sm cursor-pointer">{labels.common.close}</button>;
    $[57] = labels.common.close;
    $[58] = onClose;
    $[59] = t25;
  } else {
    t25 = $[59];
  }
  let t26;
  if ($[60] !== handleSave || $[61] !== t.save) {
    t26 = <button onClick={handleSave} className="btn-primary px-4 py-2 text-sm cursor-pointer">{t.save}</button>;
    $[60] = handleSave;
    $[61] = t.save;
    $[62] = t26;
  } else {
    t26 = $[62];
  }
  let t27;
  if ($[63] !== t25 || $[64] !== t26) {
    t27 = <div className="px-5 py-4 border-t border-border flex justify-end gap-2 shrink-0">{t25}{t26}</div>;
    $[63] = t25;
    $[64] = t26;
    $[65] = t27;
  } else {
    t27 = $[65];
  }
  let t28;
  if ($[66] !== t18 || $[67] !== t24 || $[68] !== t27) {
    t28 = <div className="bg-background rounded-xl shadow-xl w-[min(92vw,480px)] flex flex-col overflow-hidden max-h-[min(85vh,640px)]">{t18}{t24}{t27}</div>;
    $[66] = t18;
    $[67] = t24;
    $[68] = t27;
    $[69] = t28;
  } else {
    t28 = $[69];
  }
  let t29;
  if ($[70] !== isOpen || $[71] !== onClose || $[72] !== t28) {
    t29 = <Modal isOpen={isOpen} onClose={onClose}>{t28}</Modal>;
    $[70] = isOpen;
    $[71] = onClose;
    $[72] = t28;
    $[73] = t29;
  } else {
    t29 = $[73];
  }
  return t29;
}
function _temp13() {
  return "hidden";
}
function _temp12() {
  return "text-muted-foreground text-sm text-center py-3";
}
function _temp11(t0) {
  const {
    isSelected,
    isFocused: isFocused_0
  } = t0;
  return cn("px-2 rounded-md text-sm cursor-pointer", isSelected ? "bg-primary text-primary-foreground" : isFocused_0 ? "bg-muted" : "");
}
function _temp10() {
  return "max-h-60 overflow-y-auto p-1";
}
function _temp9() {
  return "bg-background border border-border rounded-lg shadow-lg mt-1 overflow-hidden";
}
function _temp8() {
  return "text-foreground text-sm flex-1 min-w-[60px]";
}
function _temp7() {
  return "text-muted-foreground text-sm";
}
function _temp6() {
  return "flex-1 flex flex-wrap items-center gap-1";
}
function _temp5(t0) {
  const {
    isFocused
  } = t0;
  return cn("flex items-center border rounded-md bg-background px-3 min-h-[38px] cursor-text text-sm", isFocused ? "border-ring ring-2 ring-ring/30" : "border-input hover:border-ring/50");
}
function _temp4(base) {
  return {
    ...base,
    zIndex: 9999
  };
}
function _temp3(o_0) {
  return o_0.value;
}
function _temp2(a, b) {
  return a.label.localeCompare(b.label);
}
function _temp(c_0) {
  return {
    value: c_0.id,
    label: getCompDisplayName(c_0),
    comp: c_0
  };
}